import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {number | undefined} */
  #inlineSuccessTimeout;

  get usesInlineQuickAdd() {
    return this.dataset.quickAddMode === 'inline_sizes';
  }

  get inlineAction() {
    return this.dataset.inlineAction || 'link';
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#cartUpdateAbortController.signal.aborted) {
      this.#cartUpdateAbortController = new AbortController();
    }

    const { signal } = this.#cartUpdateAbortController;

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal,
    });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState, { signal });
    this.addEventListener('keydown', this.#handleInlineKeydown, { signal });
    this.addEventListener('pointerdown', this.#stopInlineSliderDrag, { signal });
    this.addEventListener('mousedown', this.#stopInlineSliderDrag, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    if (this.#inlineSuccessTimeout) window.clearTimeout(this.#inlineSuccessTimeout);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = (event) => {
    this.#cachedContent.clear();

    if (!this.usesInlineQuickAdd || !this.hasAttribute('data-inline-loading')) return;
    if (!(event.target instanceof Element) || event.target.closest('quick-add-component') !== this) return;

    if (event.detail?.data?.didError) {
      this.#finishInlineAdd(false, Theme.translations?.add_to_cart_error || 'Unable to add this item');
      return;
    }

    this.#finishInlineAdd(true);
  };

  #handleCartError = (event) => {
    if (!this.usesInlineQuickAdd || !this.hasAttribute('data-inline-loading')) return;
    if (!(event.target instanceof Element) || event.target.closest('quick-add-component') !== this) return;

    const rawMessage = event.detail?.data?.message || event.detail?.data?.description;
    const message =
      typeof rawMessage === 'string'
        ? rawMessage
        : Theme.translations?.add_to_cart_error || 'Unable to add this item';
    this.#finishInlineAdd(false, message);
  };

  #handleInlineKeydown = (event) => {
    if (
      event.key !== 'Escape' ||
      !this.hasAttribute('data-inline-open') ||
      this.hasAttribute('data-inline-loading')
    )
      return;

    event.preventDefault();
    event.stopPropagation();
    this.#closeInlinePanel(true);
  };

  #stopInlineSliderDrag = (event) => {
    if (this.usesInlineQuickAdd) event.stopPropagation();
  };

  #announceInlineStatus(message) {
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    if (liveRegion instanceof HTMLElement) liveRegion.textContent = message;
  }

  #showInlineError(message = '') {
    const errorMessage = this.querySelector('[data-quick-add-inline-error]');
    if (errorMessage instanceof HTMLElement) {
      errorMessage.textContent = message;
      errorMessage.toggleAttribute('hidden', !message);
    }

    this.#announceInlineStatus(message);
  }

  #syncInlineTriggerLabel() {
    const trigger = this.querySelector('[data-inline-trigger]');
    if (!(trigger instanceof HTMLButtonElement)) return;

    let label = trigger.dataset.labelDefault || '';
    if (this.hasAttribute('data-inline-success')) {
      label = trigger.dataset.labelSuccess || label;
    } else if (this.hasAttribute('data-inline-loading')) {
      label = trigger.dataset.labelLoading || label;
    } else if (this.hasAttribute('data-inline-open')) {
      label = trigger.dataset.labelClose || label;
    }

    trigger.setAttribute('aria-label', label);
  }

  #setInlineBusy(isBusy, selectedButton = null) {
    this.toggleAttribute('data-inline-loading', isBusy);
    const trigger = this.querySelector('[data-inline-trigger]');

    if (isBusy) {
      if (this.#inlineSuccessTimeout) window.clearTimeout(this.#inlineSuccessTimeout);
      this.removeAttribute('data-inline-success');
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }

    if (trigger instanceof HTMLButtonElement) {
      trigger.disabled = trigger.dataset.available !== 'true';
      if (isBusy) {
        trigger.setAttribute('aria-disabled', 'true');
        trigger.setAttribute('aria-busy', 'true');
      } else {
        trigger.removeAttribute('aria-disabled');
        trigger.removeAttribute('aria-busy');
      }
    }

    for (const button of this.querySelectorAll('[data-quick-add-size-button]')) {
      if (!(button instanceof HTMLButtonElement)) continue;

      button.disabled = isBusy || button.dataset.available !== 'true';
      if (isBusy && button === selectedButton) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    }

    this.#syncInlineTriggerLabel();
  }

  #showInlineSuccess() {
    if (this.#inlineSuccessTimeout) window.clearTimeout(this.#inlineSuccessTimeout);

    const trigger = this.querySelector('[data-inline-trigger]');
    const successLabel =
      trigger instanceof HTMLButtonElement
        ? trigger.dataset.labelSuccess || Theme.translations?.added || 'Added'
        : Theme.translations?.added || 'Added';

    this.setAttribute('data-inline-success', '');
    if (trigger instanceof HTMLButtonElement) trigger.setAttribute('aria-disabled', 'true');
    this.#syncInlineTriggerLabel();
    this.#announceInlineStatus(successLabel);

    this.#inlineSuccessTimeout = window.setTimeout(() => {
      this.removeAttribute('data-inline-success');
      if (trigger instanceof HTMLButtonElement) trigger.removeAttribute('aria-disabled');
      this.#syncInlineTriggerLabel();
      this.#inlineSuccessTimeout = undefined;
    }, 1000);
  }

  #openInlinePanel() {
    if (!this.usesInlineQuickAdd || this.inlineAction !== 'sizes') return;

    for (const component of document.querySelectorAll('quick-add-component[data-inline-open]')) {
      if (component !== this && component instanceof QuickAddComponent) component.closeInlineSizes();
    }

    const panel = this.querySelector('.quick-add__size-panel');
    const trigger = this.querySelector('[data-inline-trigger]');
    if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) return;

    this.setAttribute('data-inline-open', '');
    this.setAttribute('stay-visible', '');
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    this.#showInlineError('');
    this.#syncInlineTriggerLabel();
  }

  #closeInlinePanel(restoreFocus) {
    const panel = this.querySelector('.quick-add__size-panel');
    const trigger = this.querySelector('[data-inline-trigger]');

    this.removeAttribute('data-inline-open');
    this.removeAttribute('stay-visible');
    panel?.setAttribute('inert', '');
    panel?.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
    this.#syncInlineTriggerLabel();

    if (restoreFocus && trigger instanceof HTMLButtonElement) trigger.focus();
  }

  #finishInlineAdd(success, errorMessage = '') {
    this.#setInlineBusy(false);

    if (success) {
      this.#showInlineError('');
      this.#closeInlinePanel(true);
      this.#showInlineSuccess();
      return;
    }

    if (this.inlineAction === 'sizes') this.#openInlinePanel();
    this.#showInlineError(errorMessage || Theme.translations?.add_to_cart_error || 'Unable to add this item');
  }

  closeInlineSizes = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    this.#closeInlinePanel(Boolean(event));
  };

  #submitInlineDirect() {
    if (this.hasAttribute('data-inline-loading') || this.hasAttribute('data-inline-success')) return;

    const trigger = this.querySelector('[data-inline-trigger]');
    const variantInput = this.querySelector('input[name="id"]');
    const form = trigger?.closest('form');
    if (
      !(trigger instanceof HTMLButtonElement) ||
      trigger.disabled ||
      !(variantInput instanceof HTMLInputElement) ||
      !variantInput.value ||
      !(form instanceof HTMLFormElement)
    )
      return;
    this.#showInlineError('');
    this.#announceInlineStatus(trigger.dataset.labelLoading || 'Adding');
    this.#setInlineBusy(true);
    form.requestSubmit();
  }

  selectInlineVariant = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasAttribute('data-inline-loading')) return;

    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = button.dataset.variantId;
    const variantInput = this.querySelector('input[name="id"]');
    const quantityInput = this.querySelector('input[name="quantity"]');
    const form = button.closest('form');
    if (!variantId || !(variantInput instanceof HTMLInputElement) || !(form instanceof HTMLFormElement)) return;

    const trigger = this.querySelector('[data-inline-trigger]');
    const loadingLabel =
      trigger instanceof HTMLButtonElement ? trigger.dataset.labelLoading || 'Adding' : 'Adding';

    this.#showInlineError('');
    this.#announceInlineStatus(loadingLabel);
    variantInput.value = variantId;
    variantInput.disabled = false;
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = button.dataset.quantityMin || '1';

    if (trigger instanceof HTMLButtonElement) trigger.focus();
    this.#setInlineBusy(true, button);
    form.requestSubmit();
  };

  /**
   * Updates contextual size variants after a product-card option changes.
   * @param {Document | Element} newHtml
   */
  updateInlineOptions(newHtml) {
    if (!this.usesInlineQuickAdd || this.inlineAction !== 'sizes') return;

    const productId = CSS.escape(this.dataset.productId || '');
    const currentOptions = this.querySelector('[data-quick-add-inline-options]');
    const newOptions =
      newHtml.querySelector(`[data-quick-add-inline-options][data-product-id="${productId}"]`) ||
      newHtml.querySelector('[data-quick-add-inline-options]');

    if (currentOptions && newOptions) morph(currentOptions, newOptions);
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.usesInlineQuickAdd) {
      event.stopPropagation();
      if (this.hasAttribute('data-inline-loading') || this.hasAttribute('data-inline-success')) return;

      if (this.inlineAction === 'sizes') {
        if (this.hasAttribute('data-inline-open')) {
          this.#closeInlinePanel(true);
        } else {
          this.#openInlinePanel();
        }
      } else if (this.inlineAction === 'direct') {
        this.#submitInlineDirect();
      } else {
        const productPageUrl = this.productPageUrl;
        if (productPageUrl) window.location.assign(productPageUrl);
      }
      return;
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (this.usesInlineQuickAdd) return null;
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.usesInlineQuickAdd) {
      this.setAttribute('data-quick-add-button', 'inline');
      return;
    }
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
